"use client";

import { useCallback, useRef, useState } from "react";

/**
 * Undo/redo as a side-car over an externally-owned value: the live value stays
 * the source of truth (here, the operation options); this only records
 * checkpoints and replays them through `apply`.
 *
 * `record(key)` is called *before* a change to checkpoint the current value.
 * The `key` coalesces a burst of related edits into one undo step:
 * - same key as the open group  → no new checkpoint (the group already has one),
 * - different key (or `null`)    → a fresh checkpoint.
 * Call `endGroup()` when a gesture finishes (e.g. pointer-up, or a debounce
 * after the last nudge) so the next edit starts a new step.
 *
 * Discrete edits pass `null`; a drag passes a stable key for the whole gesture.
 */
export function useUndoHistory<T>(value: T, apply: (next: T) => void) {
  const valueRef = useRef(value);
  valueRef.current = value;

  const past = useRef<T[]>([]);
  const future = useRef<T[]>([]);
  const groupKey = useRef<string | null>(null);
  const [, force] = useState(0);
  const bump = () => force((n) => n + 1);

  const record = useCallback((key: string | null = null) => {
    if (key !== null && key === groupKey.current) return; // group already checkpointed
    past.current.push(valueRef.current);
    future.current = [];
    groupKey.current = key;
    bump();
  }, []);

  const endGroup = useCallback(() => {
    groupKey.current = null;
  }, []);

  const undo = useCallback(() => {
    const prev = past.current.pop();
    if (prev === undefined) return;
    future.current.unshift(valueRef.current);
    groupKey.current = null;
    apply(prev);
    bump();
  }, [apply]);

  const redo = useCallback(() => {
    const next = future.current.shift();
    if (next === undefined) return;
    past.current.push(valueRef.current);
    groupKey.current = null;
    apply(next);
    bump();
  }, [apply]);

  const reset = useCallback(() => {
    past.current = [];
    future.current = [];
    groupKey.current = null;
    bump();
  }, []);

  return {
    record,
    endGroup,
    undo,
    redo,
    reset,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}

export type UndoHistory = ReturnType<typeof useUndoHistory>;
